import React, { useCallback, useEffect, useMemo, useState } from "react";
import { isEqual } from "lodash";
import { FilterPopup } from "../../../../../../Components/FilterPopup/filterPopup.index";
import { useInsights } from "../../../../../../Context/insightContext";
import { useInsightsUtils } from "../../../../../../hooks/useInsightsUtils";
import { InsightsFilterPopupProps, InsightsUiFieldFilter, InsightsUiFilters } from "../../insightsHeader.models";
import {
    cloneInsightsUiFilters,
    getInsightsFilterOptions,
    sanitizeInsightsUiFilters,
    toInsightsCreatedOnRange,
    toInsightsFilterModels,
} from "../../insightsHeader.utils";

export const InsightsFilterPopup = ({ isOpen, onClose }: InsightsFilterPopupProps) => {
    const { insightsUiFilters, setInsightsUiFilters, filterInfoStatic } = useInsights();
    const { HandleFilterChange } = useInsightsUtils();
    const [draftFilters, setDraftFilters] = useState<InsightsUiFilters>(insightsUiFilters);

    useEffect(() => {
        if (isOpen) {
            setDraftFilters(cloneInsightsUiFilters(insightsUiFilters));
        }
    }, [isOpen, insightsUiFilters]);

    const availableFilters = filterInfoStatic.current ?? [];

    const updateDraft = useCallback(
        (field: string, patch: Partial<InsightsUiFieldFilter>) => {
            setDraftFilters((prev) => {
                const operator = patch.operator ?? prev[field]?.operator;
                if (!operator) {
                    return prev;
                }

                return {
                    ...prev,
                    [field]: {
                        ...prev[field],
                        ...patch,
                        operator,
                    },
                };
            });
        },
        []
    );

    const filterOptions = useMemo(
        () => getInsightsFilterOptions(availableFilters, draftFilters, updateDraft),
        [availableFilters, draftFilters, updateDraft]
    );

    const handleApplyClick = useCallback(async () => {
        const filtersToApply = sanitizeInsightsUiFilters(draftFilters);

        if (isEqual(filtersToApply, insightsUiFilters)) {
            onClose();
            return;
        }

        const filterModels = toInsightsFilterModels(filtersToApply, availableFilters);
        const dateRange = toInsightsCreatedOnRange(filtersToApply);
        const didApply = await HandleFilterChange(filterModels, dateRange);
        if (didApply) {
            setInsightsUiFilters(filtersToApply);
        }
        onClose();
    }, [
        draftFilters,
        insightsUiFilters,
        availableFilters,
        HandleFilterChange,
        setInsightsUiFilters,
        onClose,
    ]);

    return (
        <FilterPopup
            isOpen={isOpen}
            onClose={onClose}
            filterOptions={filterOptions}
            onApply={handleApplyClick}
        />
    );
};
